import React, { useMemo, useState } from "react";
import { Grid, lighten, Link, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Alert } from "@material-ui/lab";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { SubHeader } from "src/apps/commons/dataDisplays";
import { ISSWUploadPreviewResp, SSWImportWarningType } from "../types";

const useStyles = makeStyles(theme => ({
  ul: {
    marginTop: theme.spacing(1),
    marginBottom: 0,
    "&> ul": {
      marginTop: 4,
      marginBottom: 4
    }
  },
  expander: {
    marginLeft: theme.spacing(1)
  }
}));

const MessageList = (props: {
  title: string;
  messages: string[];
  severity: "error" | "warning" | "info";
  children?: React.ReactNode;
}) => {
  const classes = useStyles();
  const { messages, severity, title, children } = props;

  const defaultExpanded = severity !== "warning";
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Alert severity={severity}>
      <Typography variant="subtitle2">
        <strong>{title}</strong>
        <Link
          href="#"
          onClick={() => setExpanded(!expanded)}
          className={classes.expander}
        >
          {expanded ? "Collapse" : "Expand"}
        </Link>
      </Typography>
      {expanded && (
        <ul className={classes.ul}>
          {children}
          {messages.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
    </Alert>
  );
};

const SubMessageList = (props: { title: string; messages: string[] }) => {
  const classes = useStyles();
  const { messages, title } = props;

  const defaultExpanded = false;
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <>
      <li>
        <strong>{title}</strong>
        <span>{` (${messages.length})`}</span>
        <Link
          href="#"
          onClick={() => setExpanded(!expanded)}
          className={classes.expander}
        >
          {expanded ? "Collapse" : "Expand"}
        </Link>
      </li>
      {expanded && (
        <ul>
          {messages.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------

const useTableStyles = makeStyles(theme => {
  const getBackgroundColor = (color: string) => lighten(color, 0.85);
  const getHoverBackgroundColor = (color: string) => lighten(color, 0.75);
  return {
    root: {
      "& .row-theme--UPDATE": {
        backgroundColor: getBackgroundColor(theme.palette.info.main),
        "&:hover": {
          backgroundColor: getHoverBackgroundColor(theme.palette.info.main)
        }
      },
      "& .row-theme--INSERT": {
        backgroundColor: getBackgroundColor(theme.palette.success.main),
        "&:hover": {
          backgroundColor: getHoverBackgroundColor(theme.palette.success.main)
        }
      },
      "& .row-theme--IGNORE": {
        backgroundColor: getBackgroundColor(theme.palette.grey.A400),
        "&:hover": {
          backgroundColor: getHoverBackgroundColor(theme.palette.grey.A400)
        }
      }
    }
  };
});

function idToString<T>(
  id: string | undefined,
  dataMap: Record<UUID, T>,
  dataToString: (data: T) => string
) {
  if (id) {
    const data = dataMap[id];
    return data ? dataToString(data) : "";
  } else {
    return "";
  }
}

const formatName = (d: { nameTh: string; nameEn: string }) =>
  `${d.nameTh} (${d.nameEn})`;
const formatCodeName = (d: { code: string; nameTh: string; nameEn: string }) =>
  `${d.code} : ${d.nameTh} (${d.nameEn})`;

const PreviewTable = (props: { data: ISSWUploadPreviewResp }) => {
  const classes = useTableStyles();
  const { data } = props;

  const [pageSize, setPageSize] = useState(10);
  const itemsWithId = useMemo(
    () =>
      data.items.map((it, idx) => ({
        ...it,
        id: idx,
        partnerBrand: idToString(
          it.partnerBrandId,
          data.partnerBrandMap,
          formatName
        ),
        partnerCategoryLv1: idToString(
          it.partnerCategoryLv1Id,
          data.partnerCategoryLv1Map,
          formatCodeName
        ),
        partnerCategoryLv2: idToString(
          it.partnerCategoryLv2Id,
          data.partnerCategoryLv2Map,
          formatCodeName
        ),
        partnerCategoryLv3: idToString(
          it.partnerCategoryLv3Id,
          data.partnerCategoryLv3Map,
          formatCodeName
        ),
        createPartnerCat: it.createPartnerCategoryLv3Code
          ? `COTTO > COTTO > ${it.createPartnerCategoryLv3Code}`
          : "",
        remyndBrand: idToString(
          it.remyndBrandId,
          data.remyndBrandMap,
          formatName
        ),
        remyndCategoryLv1: idToString(
          it.remyndCategoryLv1Id,
          data.remyndCategoryLv1Map,
          formatCodeName
        ),
        remyndCategoryLv2: idToString(
          it.remyndCategoryLv2Id,
          data.remyndCategoryLv2Map,
          formatCodeName
        ),
        remyndCategoryLv3: idToString(
          it.remyndCategoryLv3Id,
          data.remyndCategoryLv3Map,
          formatCodeName
        )
      })),
    [data]
  );
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "mode",
        headerName: "Mode",
        align: "center",
        width: 120
      },
      {
        field: "zcode",
        headerName: "ZCode",
        width: 220
      },
      {
        field: "nameEn",
        headerName: "Name (EN)",
        width: 300
      },
      {
        field: "nameTh",
        headerName: "Name (TH)",
        width: 300
      },
      {
        field: "ean",
        headerName: "EAN",
        width: 160
      },
      {
        field: "matGroup4",
        headerName: "MatGroup4",
        width: 130
      },
      {
        field: "active",
        headerName: "Active",
        type: "boolean",
        width: 110
      },
      {
        field: "cancel",
        headerName: "Cancel",
        type: "boolean",
        width: 110
      },
      {
        field: "partnerBrand",
        headerName: "PartnerBrand",
        width: 180
      },
      {
        field: "partnerCategoryLv1",
        headerName: "PartnerCatLv1",
        width: 200
      },
      {
        field: "partnerCategoryLv2",
        headerName: "PartnerCatLv2",
        width: 200
      },
      {
        field: "partnerCategoryLv3",
        headerName: "PartnerCatLv3",
        width: 200
      },
      {
        field: "createPartnerCat",
        headerName: "AutoCreatePartnerCat",
        width: 240
      },
      {
        field: "remyndBrand",
        headerName: "RemyndBrand",
        width: 180
      },
      {
        field: "remyndCategoryLv1",
        headerName: "RemyndCatLv1",
        width: 200
      },
      {
        field: "remyndCategoryLv2",
        headerName: "RemyndCatLv2",
        width: 200
      },
      {
        field: "remyndCategoryLv3",
        headerName: "RemyndCatLv3",
        width: 200
      }
    ],
    []
  );

  return (
    <div className={classes.root}>
      <DataGrid
        rows={itemsWithId}
        columns={columns}
        density="compact"
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        rowsPerPageOptions={[10, 50, 100]}
        disableSelectionOnClick
        disableColumnMenu
        autoHeight
        components={{
          Toolbar: GridToolbar
        }}
        getRowClassName={params =>
          `row-theme--${params.getValue(params.id, "mode")}`
        }
      />
    </div>
  );
};

// ---------------------------------------------------------------------------

type Props = {
  data: ISSWUploadPreviewResp;
};

const warningTypeLabelMap: Record<SSWImportWarningType, string> = {
  ZCODE_NOT_FOUND_IN_SV:
    "Basic View: ZCode not found in Sales View (products will not be imported)",
  ZCODE_FOUND_IN_SV_NO_MG4: "Sales View: unspecified MatGroup4",
  ZCODE_DUPE_IN_SV_DIFF_MG4: "Sales View: duplicate ZCode, different MatGroup4",
  ZCODE_DUPE_IN_SV_SAME_MG4: "Sales View: duplicate ZCode, same MatGroup4"
};

const UploadSSWPreview: React.FC<Props> = props => {
  const { data } = props;
  const { errors, warnings, warningGroups, infos } = data;
  const warningTypes = Object.keys(warningGroups) as SSWImportWarningType[];
  const totalWarnings =
    warnings.length +
    warningTypes.reduce((s, k) => s + warningGroups[k].length, 0);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SubHeader title="Summary & Preview" marginTop />
      </Grid>

      {errors.length > 0 && (
        <Grid item xs={12}>
          <MessageList
            title={`Cannot import data, please fix these errors first (${errors.length})`}
            messages={errors}
            severity="error"
          />
        </Grid>
      )}
      {infos.length > 0 && (
        <Grid item xs={12}>
          <MessageList title={`Summary`} messages={infos} severity="info" />
        </Grid>
      )}
      {totalWarnings > 0 && (
        <Grid item xs={12}>
          <MessageList
            title={`Warnings (${totalWarnings})`}
            messages={warnings}
            severity="warning"
          >
            {warningTypes.map(k => (
              <SubMessageList
                key={k}
                title={warningTypeLabelMap[k]}
                messages={warningGroups[k]}
              />
            ))}
          </MessageList>
        </Grid>
      )}

      <Grid item xs={12}>
        <PreviewTable data={data} />
      </Grid>
    </Grid>
  );
};

export default UploadSSWPreview;
